import { supabase } from '../lib/supabase';

export const marketingService = {
  // Get marketing metrics for date range
  async getMetrics(startDate, endDate) {
    let query = supabase?.from('marketing_metrics')?.select('*')?.order('metric_date', { ascending: true });

    if (startDate) {
      query = query?.gte('metric_date', startDate);
    }
    if (endDate) {
      query = query?.lte('metric_date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      metricDate: row?.metric_date,
      websiteVisits: row?.website_visits,
      conversionRate: row?.conversion_rate,
      onlineBookings: row?.online_bookings,
      bookingConversionRate: row?.booking_conversion_rate,
      patientAcquisitionCost: row?.patient_acquisition_cost,
      roiPercentage: row?.roi_percentage,
      newPatients: row?.new_patients,
      returningPatients: row?.returning_patients,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Get online bookings
  async getBookings(status = null) {
    let query = supabase?.from('online_bookings')?.select(`
        *,
        patient:patient_id(id, full_name, email),
        provider:provider_id(id, full_name)
      `)?.order('appointment_date', { ascending: true });

    if (status) {
      query = query?.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      patientId: row?.patient_id,
      providerId: row?.provider_id,
      bookingSource: row?.booking_source,
      appointmentType: row?.appointment_type,
      appointmentDate: row?.appointment_date,
      status: row?.status,
      patientName: row?.patient_name,
      patientEmail: row?.patient_email,
      patientPhone: row?.patient_phone,
      notes: row?.notes,
      confirmationSentAt: row?.confirmation_sent_at,
      patient: row?.patient,
      provider: row?.provider,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Get automated reminders
  async getReminders(status = null) {
    let query = supabase?.from('automated_reminders')?.select(`
        *,
        booking:booking_id(id, patient_name, appointment_date),
        patient:patient_id(id, full_name)
      `)?.order('scheduled_time', { ascending: true });

    if (status) {
      query = query?.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      bookingId: row?.booking_id,
      patientId: row?.patient_id,
      reminderType: row?.reminder_type,
      channel: row?.channel,
      scheduledTime: row?.scheduled_time,
      sentTime: row?.sent_time,
      status: row?.status,
      messageTemplate: row?.message_template,
      messageContent: row?.message_content,
      deliveryStatus: row?.delivery_status,
      errorMessage: row?.error_message,
      booking: row?.booking,
      patient: row?.patient,
      createdAt: row?.created_at
    }));
  },

  // Get online reviews
  async getReviews(platform = null) {
    let query = supabase?.from('online_reviews')?.select(`
        *,
        patient:patient_id(id, full_name),
        responder:responded_by(id, full_name)
      `)?.order('review_date', { ascending: false });

    if (platform) {
      query = query?.eq('platform', platform);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      patientId: row?.patient_id,
      platform: row?.platform,
      rating: row?.rating,
      reviewText: row?.review_text,
      reviewerName: row?.reviewer_name,
      reviewDate: row?.review_date,
      responseText: row?.response_text,
      responseDate: row?.response_date,
      respondedBy: row?.responded_by,
      sentimentScore: row?.sentiment_score,
      isFeatured: row?.is_featured,
      patient: row?.patient,
      responder: row?.responder,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Get marketing campaigns
  async getCampaigns(status = null) {
    let query = supabase?.from('marketing_campaigns')?.select(`
        *,
        creator:created_by(id, full_name)
      `)?.order('created_at', { ascending: false });

    if (status) {
      query = query?.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      campaignName: row?.campaign_name,
      campaignType: row?.campaign_type,
      targetAudience: row?.target_audience,
      status: row?.status,
      startDate: row?.start_date,
      endDate: row?.end_date,
      messageTemplate: row?.message_template,
      channel: row?.channel,
      totalRecipients: row?.total_recipients,
      sentCount: row?.sent_count,
      deliveredCount: row?.delivered_count,
      openedCount: row?.opened_count,
      clickedCount: row?.clicked_count,
      conversionCount: row?.conversion_count,
      budget: row?.budget,
      spent: row?.spent,
      createdBy: row?.created_by,
      creator: row?.creator,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    }));
  },

  // Create new booking
  async createBooking(bookingData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('online_bookings')?.insert({
        patient_id: user?.id,
        provider_id: bookingData?.providerId,
        booking_source: bookingData?.bookingSource,
        appointment_type: bookingData?.appointmentType,
        appointment_date: bookingData?.appointmentDate,
        patient_name: bookingData?.patientName,
        patient_email: bookingData?.patientEmail,
        patient_phone: bookingData?.patientPhone,
        notes: bookingData?.notes
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Update booking status
  async updateBookingStatus(bookingId, status) {
    const { data, error } = await supabase?.from('online_bookings')?.update({ status, updated_at: new Date()?.toISOString() })?.eq('id', bookingId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Respond to review
  async respondToReview(reviewId, responseText) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('online_reviews')?.update({
        response_text: responseText,
        response_date: new Date()?.toISOString(),
        responded_by: user?.id,
        updated_at: new Date()?.toISOString()
      })?.eq('id', reviewId)?.select()?.single();

    if (error) throw error;
    return data;
  }
};